import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Card, CardContent } from "@/shared/components/ui/card";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { Switch } from "@/shared/components/ui/switch";
import { Button } from "@/shared/components/ui/button";
import { useToast } from "@/shared/hooks/use-toast";
import {
  SettingsService,
  SettingItem,
} from "@/shared/services/api/SettingsService";
import { post } from "@/shared/utils/fetchApi";

interface FeedbackSettingsValue {
  enabled: boolean;
  session_timeout_minutes: number;
  session_count_threshold: number;
  ga4_enabled?: boolean;
  ga4_event_name?: string;
}

export const FeedbackSettings = () => {
  const { t } = useTranslation();
  const { toast } = useToast();

  const [currentSetting, setCurrentSetting] = useState<SettingItem | null>(
    null,
  );
  const [formValue, setFormValue] = useState<FeedbackSettingsValue>({
    enabled: true,
    session_timeout_minutes: 15,
    session_count_threshold: 0,
    ga4_enabled: true,
    ga4_event_name: "user_feedback",
  });

  const { data, isLoading } = useQuery({
    queryKey: ["feedback_settings"],
    queryFn: async () => {
      const setting =
        await SettingsService.getRawSettingByType("feedback_form");
      return setting;
    },
  });

  useEffect(() => {
    if (data) {
      setCurrentSetting(data);
      const value = (data.value || {}) as Partial<FeedbackSettingsValue>;
      setFormValue({
        // backward-compat: rows persisted before this field default to true
        enabled: value.enabled ?? true,
        session_timeout_minutes: value.session_timeout_minutes ?? 15,
        session_count_threshold: value.session_count_threshold ?? 0,
        ga4_enabled: value.ga4_enabled ?? true,
        ga4_event_name: value.ga4_event_name ?? "user_feedback",
      });
    }
  }, [data]);

  const mutation = useMutation({
    mutationFn: async (payload: FeedbackSettingsValue) => {
      if (!currentSetting) {
        throw new Error("Feedback setting not loaded");
      }
      return SettingsService.updateSetting(currentSetting.id, {
        value: payload,
      });
    },
    onSuccess: () => {
      toast({
        title: t("settings.feedback.updateSuccess", {
          defaultValue: "Feedback settings updated",
        }),
        variant: "default",
      });
    },
    onError: (error: unknown) => {
      toast({
        title: t("common.error"),
        description:
          error instanceof Error
            ? error.message
            : t("settings.feedback.updateError", {
                defaultValue: "Failed to update feedback settings",
              }),
        variant: "destructive",
      });
    },
  });

  const resetMutation = useMutation({
    mutationFn: async () => {
      // v2 retorna flat { updatedCount } — re-envelopa p/ manter o contrato do onSuccess
      const r = await post<{ updatedCount?: number }>(
        "/feedback/admin/reset",
      );
      return { success: true, data: { updatedCount: r?.updatedCount } };
    },
    onSuccess: (response) => {
      const count = response?.data?.updatedCount;
      toast({
        title: t("settings.feedback.resetSuccess", {
          defaultValue: "Feedback reset successfully",
        }),
        description:
          typeof count === "number"
            ? t("settings.feedback.resetCount", {
                defaultValue: "{{count}} profiles were reset.",
                count,
              })
            : undefined,
        variant: "default",
      });
    },
    onError: (error: unknown) => {
      toast({
        title: t("common.error"),
        description:
          error instanceof Error
            ? error.message
            : t("settings.feedback.resetError", {
                defaultValue: "Failed to reset user feedback",
              }),
        variant: "destructive",
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    mutation.mutate(formValue);
  };

  const handleNumberChange = (
    field: keyof FeedbackSettingsValue,
    value: string,
  ) => {
    const numeric = Number(value);
    if (Number.isNaN(numeric)) {
      return;
    }
    setFormValue((prev) => ({ ...prev, [field]: numeric }));
  };

  const handleResetFeedback = () => {
    resetMutation.mutate();
  };

  if (isLoading) {
    return (
      <Card className="w-full">
        <CardContent className="pt-6">
          <p className="text-sm text-muted-foreground">{t("common.loading")}</p>
        </CardContent>
      </Card>
    );
  }

  if (!currentSetting) {
    return (
      <Card className="w-full">
        <CardContent className="pt-6">
          <p className="text-sm text-muted-foreground">
            {t("settings.feedback.missingConfig", {
              defaultValue:
                "Run the database migration that creates the feedback_form setting before editing here.",
            })}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="w-full">
      <CardContent className="pt-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="flex items-center justify-between space-x-4 rounded-md border p-3">
            <div className="space-y-1">
              <Label htmlFor="feedback_enabled">
                {t("settings.feedback.enabled", {
                  defaultValue: "Enable Feedback",
                })}
              </Label>
              <p className="text-xs text-muted-foreground">
                {t("settings.feedback.enabledDescription", {
                  defaultValue:
                    "When disabled, the feedback popup is hidden for everyone — including users who never responded.",
                })}
              </p>
            </div>
            <Switch
              id="feedback_enabled"
              checked={!!formValue.enabled}
              onCheckedChange={(checked) =>
                setFormValue((prev) => ({ ...prev, enabled: checked }))
              }
            />
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-1">
              <Label htmlFor="session_timeout_minutes">
                {t("settings.feedback.sessionTimeout", {
                  defaultValue: "Session timeout (minutes)",
                })}
              </Label>
              <Input
                id="session_timeout_minutes"
                type="number"
                min={0}
                value={formValue.session_timeout_minutes}
                onChange={(e) =>
                  handleNumberChange("session_timeout_minutes", e.target.value)
                }
              />
              <p className="text-xs text-muted-foreground">
                {t("settings.feedback.sessionTimeoutDescription", {
                  defaultValue:
                    "Time after login before the feedback popup is shown. 0 disables this trigger.",
                })}
              </p>
            </div>

            <div className="space-y-1">
              <Label htmlFor="session_count_threshold">
                {t("settings.feedback.sessionCountThreshold", {
                  defaultValue: "Session count threshold (logins)",
                })}
              </Label>
              <Input
                id="session_count_threshold"
                type="number"
                min={0}
                step={1}
                value={formValue.session_count_threshold}
                onChange={(e) =>
                  handleNumberChange("session_count_threshold", e.target.value)
                }
              />
              <p className="text-xs text-muted-foreground">
                {t("settings.feedback.sessionCountThresholdDescription", {
                  defaultValue:
                    "Show the form once the user reaches this many logins. Combined with the timeout via OR — whichever triggers first wins. 0 disables this trigger.",
                })}
              </p>
            </div>
          </div>

          <div className="flex items-center justify-between space-x-4 rounded-md border p-3">
            <div className="space-y-1">
              <Label htmlFor="ga4_enabled">
                {t("settings.feedback.ga4Enabled", {
                  defaultValue: "Enable GA4 events",
                })}
              </Label>
              <p className="text-xs text-muted-foreground">
                {t("settings.feedback.ga4EnabledDescription", {
                  defaultValue:
                    "When enabled, the frontend will send GA4 events for impression, submission and dismissal.",
                })}
              </p>
            </div>
            <Switch
              id="ga4_enabled"
              checked={!!formValue.ga4_enabled}
              onCheckedChange={(checked) =>
                setFormValue((prev) => ({ ...prev, ga4_enabled: checked }))
              }
            />
          </div>

          <div className="space-y-1">
            <Label htmlFor="ga4_event_name">
              {t("settings.feedback.ga4EventName", {
                defaultValue: "GA4 event name",
              })}
            </Label>
            <Input
              id="ga4_event_name"
              value={formValue.ga4_event_name || ""}
              onChange={(e) =>
                setFormValue((prev) => ({
                  ...prev,
                  ga4_event_name: e.target.value,
                }))
              }
            />
          </div>

          <div className="flex justify-end">
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending
                ? t("common.saving", { defaultValue: "Saving..." })
                : t("common.save", { defaultValue: "Save" })}
            </Button>
          </div>

          <div className="mt-8 space-y-2 rounded-md border p-3">
            <div className="flex items-center justify-between gap-4">
              <div className="space-y-1">
                <Label>
                  {t("settings.feedback.resetTitle", {
                    defaultValue: "Reset user feedback",
                  })}
                </Label>
                <p className="text-xs text-muted-foreground">
                  {t("settings.feedback.resetDescription", {
                    defaultValue:
                      "Clear all submitted answers and reopen the feedback form for every user. Historical events are preserved.",
                  })}
                </p>
              </div>
              <Button
                type="button"
                variant="destructive"
                onClick={handleResetFeedback}
                disabled={resetMutation.isPending}
              >
                {resetMutation.isPending
                  ? t("settings.feedback.resetting", {
                      defaultValue: "Resetting...",
                    })
                  : t("settings.feedback.resetButton", {
                      defaultValue: "Reset Feedback",
                    })}
              </Button>
            </div>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};
